<script>
	// NPM Modules
	import { navigate } from 'svelte-navaid';
	import { tick } from 'svelte';
	import { afterUpdate } from 'svelte';
	// Stores
	import UserStore from '@/Stores/UserStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	import { triggerUserSearch } from '@/Stores/Store.js';
	import PreferenceStore from '@/Stores/PreferenceStore.js';
	// Components
	import Icon from '@/Components/Icon.svelte';
	import Loading from '@/Components/Loading.svelte';
	import Label from '@/Components/Label.svelte';
	import Helmet from '@/Components/Helmet.svelte';
	// Partials
	import Profile from '@/Partials/User/Profile.svelte';
	import BetHistory from '@/Partials/User/BetHistory.svelte';
	import BonusBets from '@/Partials/User/BonusBets.svelte';
	import PhoneBet from '@/Partials/User/PhoneBet.svelte';
	import BetBack from '@/Partials/User/BetBack.svelte';
	import WarningMessage from '@/Partials/User/_WarningMessage.svelte';
	import UpdateRating from '@/Partials/User/_UpdateRating.svelte';
	import UpdateManager from '@/Partials/User/_UpdateManager.svelte';
	// Constants
	import {
		UserRatingColours,
		AccountTypes,
		UserSubRatingColours,
		UserRating,
		UserStageColours
	} from '@/Constants/User.js';
	import Promotions from '@/Partials/User/PromotionHistory.svelte';
	// Services
	import { CurrencyFormat, NumberFormat } from '@/Services/Helpers.js';

	export let id;
	export let action = $PreferenceStore.app.userInitialTab;

	const actionComponent = {
		profile: Profile,
		'bet-history': BetHistory,
		'bonus-bets': BonusBets,
		'phone-bet': PhoneBet,
		'bet-back': BetBack,
		promotions: Promotions
	};

	let loading;
	let openUpdateRating = false;
	let openUpdateManager = false;
	let showLinkedAccounts = false;
	let user;

	$: id && loadUserProfile();
	$: user = id && $UserStore && $UserStore.firstWhere('id', id.toString());
	$: meta = user && $UserStore && UserStore.getUserMeta();
	$: stats = user && user.betting_stats.data;
	$: isBetBack = user && user.account_type && user.account_type.id === AccountTypes.BETBACK;
	$: isBetBack && action == 'phone-bet' && user && user.id ? navigate(`/users/${user.id}/bet-back`) : null;
	$: !isBetBack && action == 'bet-back' && user && user.id ? navigate(`/users/${user.id}/phone-bet`) : null;
	$: bonusCostPercentage = stats && calcBonusCostPercentage();

	afterUpdate(() => {
		if ($triggerUserSearch) {
			loadUserProfile();
			triggerUserSearch.set(false);
		}
	});

	function loadUserProfile() {
		loading = UserStore.getUserWithDetails(id);
	}

	async function openUpdateManagerModal() {
		if ($ACLStore && ACLStore.granted('users.update.manager')) {
			openUpdateManager = true;
			await tick();
			window.scrollTo(0, 0);
		}
	}

	async function openLinkedAccount(userId) {
		showLinkedAccounts = false;
		navigate(`/users/${userId}`);
	}

	function calcBonusCostPercentage() {
		if (!stats) {
			return '';
		}
		return stats.sum('gross_win')
			? ((stats.sum('bonus_paid') + stats.sum('boosted_amount')) / stats.sum('gross_win')) * 100
			: 0;
	}
</script>

<Helmet title={user?.full_name ? 'User - ' + user?.full_name : 'User'} />

{#if $ACLStore && ACLStore.granted('users.update') && openUpdateRating}
	<UpdateRating {user} bind:open={openUpdateRating} />
{/if}
{#if $ACLStore && ACLStore.granted('users.update.manager') && openUpdateManager}
	<UpdateManager {user} managers={meta && meta.account_managers} bind:open={openUpdateManager} />
{/if}

{#await loading}
	<Loading />
{:then}
	{#if user}
		<div class="flex flex-col h-full w-full">
			<!-- Page Header -->
			<div class="flex flex-col items-center w-full pt-2 px-2 pb-1 md:p-4 text-sm bg-white border-b md:py-2">
				<div class="flex flex-col md:flex-row items-center w-full">
					<div class="flex flex-col gap-y-2 md:gap-y-0 md:flex-row items-center justify-between w-full">
						<div class="flex space-x-2 items-center md:w-1/3">
							<div
								class="flex flex-row md:flex-col lg:flex-row space-x-1 space-y-0 md:space-x-0 md:space-y-2 lg:space-x-2 lg:space-y-0"
							>
								<a
									href={'/users'}
									title="Classifications"
									class="flex cursor-pointer hover:underline text-grey items-center"
								>
									<Icon name="chevron-left" classes="w-2 sm:mr-1" />
									<div class="text-lg font-bold">Users</div>
								</a>
								<h1
									class="flex flex-col sm:text-base font-semibold items-start divide-gray-200 divide-y pl-1 sm:px-1.5 text-xs"
								>
									<span class="whitespace-nowrap">{user.name} - <span class="font-bold ml-1">[{user.pin}]</span></span>
									<span class="whitespace-nowrap">{user.first_name} {user.last_name}</span>
								</h1>
							</div>
							<div
								class="flex flex-row md:flex-col lg:flex-row items-center justify-center space-x-1 space-y-0 md:space-y-1 md:space-x-0 lg:space-x-2 lg:space-y-0"
							>
								<div class="flex flex-col xl:flex-row items-center justify-center space-y-1 xl:space-y-0 xl:space-x-2">
									{#if typeof user.affiliate !== 'undefined' && user.affiliate.name !== null && user.affiliate.name !== ''}
										<button
											class="flex flex-row items-center justify-center p-1 text-white align-bottom
										bg-yellow-700 hover:bg-yellow-300 rounded-md focus:outline-none whitespace-nowrap"
											title="Affiliate"
										>
											<Icon name="affiliate" classes="h-3 w-3 inline" />
											<span class="pl-1 pr-0.5">
												{user.affiliate.name}
											</span>
										</button>
									{/if}
									<button
										on:click={() => openUpdateManagerModal()}
										class="flex flex-row items-center justify-center p-1 text-white
									bg-purple-500 rounded-md hover:bg-purple-400 focus:outline-none whitespace-nowrap"
										title="Account Manager"
									>
										<Icon name="manager" classes="h-3 w-3 inline" />
										<span class="pl-1 pr-0.5">
											{(user &&
												user.account_manager_id &&
												meta &&
												meta.account_managers &&
												meta.account_managers[user.account_manager_id]) ||
												'NO MANAGER'}
										</span>
									</button>
								</div>
								<div class="flex flex-col xl:flex-row items-center justify-center space-y-1 xl:space-y-0 xl:space-x-2">
									{#if (user.link_accounts && user.link_accounts?.data?.items?.length > 0) || (user.link_accounts && user.link_accounts?.id)}
										<div
											class="relative inline-block"
											on:mouseover={() => (showLinkedAccounts = true)}
											on:mouseleave={() => (showLinkedAccounts = false)}
											on:focus={() => (showLinkedAccounts = true)}
											on:blur={() => (showLinkedAccounts = false)}
											role="tooltip"
										>
											<button
												type="button"
												class="flex flex-row items-center justify-center p-0.5 text-white bg-blue-500 rounded-md focus:outline-none whitespace-nowrap"
											>
												<Label label="Linked Accounts" classes="font-semibold whitespace-nowrap" />
											</button>
											{#if showLinkedAccounts}
												<div class="absolute z-10 pt-2">
													<div class="relative bg-white border border-gray-400 rounded-md shadow-xl">
														<div
															class="absolute top-0 w-4 h-4 origin-center transform rotate-45 translate-x-5 -translate-y-2 bg-white border-t
														border-l border-gray-400 rounded-sm pointer-events-none"
														/>
														<div class="relative flex p-3 -mr-4 whitespace-nowrap flex-col">
															{#if user.link_accounts && user.link_accounts?.data?.items?.length > 0}
																{#each user.link_accounts.data.items as linkedAccount}
																	<div class="mr-4 text-center">
																		<button
																			on:click={() => openLinkedAccount(linkedAccount.id)}
																			class="focus:outline-none"
																		>
																			<Label
																				label={linkedAccount.name +
																					' (PIN =' +
																					linkedAccount.pin +
																					') => ' +
																					linkedAccount.status.name}
																				labelForColor={linkedAccount.status.name}
																				classes="font-semibold mt-1"
																			/>
																		</button>
																	</div>
																{/each}
															{:else}
																<div class="mr-4 text-center">
																	<button
																		on:click={() => openLinkedAccount(user.link_accounts.id)}
																		class="focus:outline-none"
																	>
																		<Label
																			label={user.link_accounts.name +
																				' (PIN =' +
																				user.link_accounts.pin +
																				') => ' +
																				user.link_accounts.status.name}
																			labelForColor={user.link_accounts.status.name}
																			classes="font-semibold mt-1"
																		/>
																	</button>
																</div>
															{/if}
														</div>
													</div>
												</div>
											{/if}
										</div>
									{/if}
									{#if user.stage}
										<button
											class="flex flex-row whitespace-nowrap mt-0.5 items-center justify-center focus:outline-none rounded px-2 py-1.5 text-xs font-semibold text-center {UserStageColours[
												user.stage.id
											].bg} {UserStageColours[user.stage.id].text} "
											title="Stage"
										>
											{user.stage.name}
										</button>
									{/if}
								</div>
							</div>
						</div>

						<div class="flex items-center justify-center md:w-1/3">
							<div class="btn-group">
								{#if $ACLStore && ACLStore.granted('users.read')}
									<button
										on:click={() => navigate(`/users/${user.id}/profile`)}
										class="btn {action === 'profile' ? 'btn-primary-action' : 'btn-muted text-dark'}"
									>
										Profile
									</button>
								{/if}
								{#if $ACLStore && ACLStore.granted('bets')}
									<button
										on:click={() => navigate(`/users/${user.id}/bet-history`)}
										class="btn {action === 'bet-history' ? 'btn-primary-action' : 'btn-muted text-dark'}"
									>
										Bet History
									</button>
								{/if}
								{#if $ACLStore && ACLStore.granted('bonuses')}
									<button
										on:click={() => navigate(`/users/${user.id}/bonus-bets`)}
										class="btn {action === 'bonus-bets' ? 'btn-primary-action' : 'btn-muted text-dark'}"
									>
										Bonus Bets
									</button>
								{/if}
								{#if $ACLStore && ACLStore.granted('promotions')}
									<button
										on:click={() => navigate(`/users/${user.id}/promotions`)}
										class="btn {action === 'promotions' ? 'btn-primary-action' : 'btn-muted text-dark'}"
									>
										<span class="hidden xl:flex">Promotions</span>
										<span class="xl:hidden">Promos</span>
									</button>
								{/if}
								{#if $ACLStore && ACLStore.granted('users.login') && !isBetBack}
									<button
										on:click={() => navigate(`/users/${user.id}/phone-bet`)}
										class="btn {action === 'phone-bet' ? 'btn-primary-action' : 'btn-muted text-dark'}"
									>
										Phone Bet
									</button>
								{/if}
								{#if $ACLStore && ACLStore.granted('users.login.betback') && isBetBack}
									<button
										on:click={() => navigate(`/users/${user.id}/bet-back`)}
										class="btn {action === 'bet-back' ? 'btn-primary-action' : 'btn-muted text-dark'}"
									>
										Bet Back
									</button>
								{/if}
							</div>
						</div>
						<div
							class="flex flex-col md:flex-row items-center space-x-2 md:w-1/3 md:justify-end space-y-2 md:space-y-0"
						>
							<div class="flex flex-row space-x-4 md:space-x-2">
								<div class="flex flex-col whitespace-nowrap items-center justify-center font-normal pt-1">
									<span class="flex flex-row md:flex-col 2xl:flex-row items-center justify-center"
										><span class="md:flex hidden xl:hidden">BOCO%:</span><span
											class="md:hidden flex xl:flex justify-center items-center">Bonus Cost %:</span
										>
										{NumberFormat(bonusCostPercentage, true)}%</span
									>
								</div>
								<div class="flex flex-row space-x-3 md:space-x-0 md:flex-col-reverse 2xl:flex-row 2xl:space-x-3">
									<div class="flex flex-col whitespace-nowrap items-center justify-center font-normal pt-1">
										<span>Bonus Bal: {CurrencyFormat(user.bonus_balance)}</span>
									</div>
									<div class="flex flex-col whitespace-nowrap items-center justify-center font-semibold pt-1">
										<span>Bal: {CurrencyFormat(user.balance)}</span>
									</div>
								</div>
							</div>
							<div class="flex flex-row space-x-2 md:space-x-0.5 lg:space-x-2">
								<div class="flex flex-col items-center justify-center">
									<div
										class="flex flex-row whitespace-nowrap items-center justify-center focus:outline-none rounded text-xs text-center"
									>
										<Label label={user.status.name} classes="whitespace-nowrap" />
									</div>
									{#if user.tier.name}
										<div
											class="hidden md:flex lg:hidden flex-row whitespace-nowrap items-center justify-center focus:outline-none rounded text-xs text-center mt-1"
										>
											<Label label={user.tier.name ? '$' + user.tier.name : ''} />
										</div>
									{/if}
								</div>
								{#if $ACLStore && ACLStore.granted('users.update.user_rating')}
									<div
										class="
										flex flex-row space-y-0 space-x-2
										sm:flex-row sm:space-y-0 sm:space-x-2
										md:flex-col md:space-y-1 md:space-x-0
										2xl:flex-row 2xl:space-y-0 2xl:space-x-2
"
									>
										<div class="flex flex-col">
											<button
												on:click={() => (openUpdateRating = true)}
												class="flex flex-row whitespace-nowrap items-center justify-center focus:outline-none rounded px-2 py-1 text-xs
                  								text-center {UserRatingColours[user.rating.id].bg} {UserRatingColours[user.rating.id].text}"
												title={`Projected: ${user.projected_rating.name} - No. days ${user.rated_days}`}
											>
												{user.rating.name}
											</button>
										</div>
										{#if user.rating.id === UserRating.VALUED || user.rating.id === UserRating.VIP}
											{#if user.sub_rating}
												<button
													on:click={() => (openUpdateRating = true)}
													class="flex flex-row whitespace-nowrap items-center justify-center focus:outline-none rounded px-2 py-1 text-xs
					  								text-center {UserSubRatingColours[user.sub_rating.id].bg} {UserSubRatingColours[user.sub_rating.id].text}"
													title="Sub-Rating"
												>
													{user.sub_rating.name}
												</button>
											{/if}
										{/if}
									</div>
								{:else}
									<div
										class="flex flex-row whitespace-nowrap items-center justify-center focus:outline-none rounded px-2 py-1 text-xs
                 						text-center {UserRatingColours[user.rating.id].bg} {UserRatingColours[user.rating.id].text}"
									>
										{user.rating.name}
									</div>
									{#if user.rating.id === UserRating.VALUED || user.rating.id === UserRating.VIP}
										{#if user.sub_rating}
											<button
												class="flex flex-row whitespace-nowrap items-center justify-center focus:outline-none rounded px-2 py-1 text-xs
					  								text-center {UserSubRatingColours[user.sub_rating.id].bg} {UserSubRatingColours[user.sub_rating.id].text}"
												title="Sub-Rating"
											>
												{user.sub_rating.name}
											</button>
										{/if}
									{/if}
								{/if}
								{#if user.tier.name}
									<div
										class="md:hidden flex lg:flex flex-row whitespace-nowrap items-center justify-center focus:outline-none rounded text-xs text-center"
									>
										<Label label={user.tier.name ? '$' + user.tier.name : ''} />
									</div>
								{/if}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="mb-2">
				<WarningMessage {user} canEdit={$ACLStore && ACLStore.granted('users.update')} />
			</div>
			<!-- /Page Header -->
			<svelte:component this={actionComponent[action] ? actionComponent[action] : Profile} {user} />
		</div>
	{/if}
{/await}
